// src/Layout/Header.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Dropdown,
  Button,
  Row,
  Col,
  Card,
  Image,
  Navbar,
  Nav,
  Spinner,
} from "react-bootstrap";
import { signOut } from "firebase/auth";

import avtar1 from "assets/images/users/avatar-1.jpg";

import { CardModal, SearchModal } from "Components/MainModal";
import { withTranslation } from "react-i18next";
import withRouter from "Components/withRouter";

// storefront menu api
import { useGetStoreMenuQuery } from "../redux/services/store/storeMenusApi";

// cart api (for badge count)
import { useGetStoreCartQuery } from "../redux/services/store/storeCartApi";

// storefront settings (PUBLIC) for logo/colors
import { useGetStoreSettingsPublicQuery } from "../redux/services/store/storeSettingsApi";

// auth api
import {
  useGetStoreMeQuery,
  useStoreLogoutMutation,
} from "../redux/services/store/storeAuthApi";
import { auth } from "../config/firebase";

type MenuItem = {
  id: number;
  parent_id: number | null;
  sort_order: number;
  is_active: boolean;

  label: string;
  item_type: "route" | "url" | "page" | "divider";
  to_path?: string | null;
  url?: string | null;
  page_id?: number | null;

  meta_json?: Record<string, any> | null;
  group_key?: string | null;
  group_title?: string | null;
  column_no?: number | null;
  display_as?: string | null;

  children?: MenuItem[];
};

type BlockItem = {
  id: number;
  block_id: number;
  sort_order: number;
  is_active: boolean;
  title?: string | null;
  image_url?: string | null;
  to_path?: string | null;
  url?: string | null;
  meta_json?: Record<string, any> | null;
};

type Block = {
  id: number;
  menu_id: number;
  block_key: string;
  block_type: string;
  sort_order: number;
  is_active: boolean;

  title?: string | null;
  subtitle?: string | null;
  image_url?: string | null;
  overlay_json?: Record<string, any> | null;

  cta_label?: string | null;
  cta_to_path?: string | null;
  cta_url?: string | null;
  cta_style?: string | null;
  cta_icon?: string | null;

  html_content?: string | null;
  settings_json?: Record<string, any> | null;

  items?: BlockItem[];
};

const GUEST_KEY_STORAGE = "X_GUEST_KEY";

const createGuestKey = () => {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as any).randomUUID()
    : `guest_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const getOrCreateGuestKey = () => {
  try {
    const existing = localStorage.getItem(GUEST_KEY_STORAGE);
    if (existing && existing.trim()) return existing.trim();

    const created = createGuestKey();
    localStorage.setItem(GUEST_KEY_STORAGE, created);
    return created;
  } catch {
    return null;
  }
};

const headerSkeletonBaseStyle: React.CSSProperties = {
  background:
    "linear-gradient(90deg, rgba(0,0,0,0.06) 25%, rgba(255,255,255,0.38) 37%, rgba(0,0,0,0.06) 63%)",
  backgroundSize: "400% 100%",
  animation: "headerSkeletonShimmer 1.4s ease infinite",
};

const HeaderLogoSkeleton = ({ height = 50 }: { height?: number }) => {
  return (
    <div
      style={{
        width: 150,
        height,
        borderRadius: 6,
        ...headerSkeletonBaseStyle,
      }}
    />
  );
};

const HeaderMenuSkeleton = () => {
  const widths = [78, 108, 86, 96, 82];

  return (
    <>
      {widths.map((width, index) => (
        <li
          key={`menu-skeleton-${index}`}
          className="nav-item d-flex align-items-center px-3 py-2"
          aria-hidden="true"
        >
          <div
            style={{
              width,
              height: 16,
              borderRadius: 999,
              ...headerSkeletonBaseStyle,
            }}
          />
        </li>
      ))}
    </>
  );
};

const Header = (props: any) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [card, setCard] = useState(false);
  const handlecardClose = () => setCard(false);
  const handlecardShow = () => setCard(true);

  const [showMenu, setShowMenu] = useState<any>("");
  const [showSubMenu, setShowSubMenu] = useState<any>("");
  const [showPageSubMenu, setShowPageSubMenu] = useState<any>("");

  const currentThemeMode =
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-bs-theme") || "light"
      : "light";
  const isDarkMode = currentThemeMode === "dark";

  const handleThemeToggle = () => {
    props.handleMood(isDarkMode ? "light" : "dark");
  };

  const guestKey = useMemo(() => getOrCreateGuestKey(), []);

  const { data: settings, isFetching: settingsFetching } =
    useGetStoreSettingsPublicQuery();

  const apiHeaderLogo = (settings as any)?.header_logo_url || null;
  const headerLogoSrc = apiHeaderLogo;
  const headerLogoLightSrc = apiHeaderLogo;

  const primaryColor = (settings as any)?.primary_color || null;
  const secondaryColor = (settings as any)?.secondary_color || null;

  const navbarStyle: React.CSSProperties = useMemo(() => {
    if (!primaryColor && !secondaryColor) return {};
    return {
      ...(primaryColor ? { backgroundColor: primaryColor } : {}),
      ...(secondaryColor ? { color: secondaryColor } : {}),
    };
  }, [primaryColor, secondaryColor]);

  const linkStyle: React.CSSProperties = useMemo(() => {
    if (!secondaryColor) return {};
    return { color: secondaryColor };
  }, [secondaryColor]);

  const { data: cartData } = useGetStoreCartQuery(undefined, {
    skip: !guestKey,
  });

  const cartItemsCount = Array.isArray((cartData as any)?.items)
    ? (cartData as any).items.length
    : Array.isArray((cartData as any)?.cart?.items)
    ? (cartData as any)?.cart?.items.length
    : 0;

  const { data: storeMenu, isLoading: menuLoading, isFetching: menuFetching } =
    useGetStoreMenuQuery(
      {
        menuKey: "header_main",
        activeOnly: true,
        buildTree: true,
        includeBlocks: true,
      },
      {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
      }
    );

  const {
    data: me,
    isFetching: meFetching,
    isError: meError,
  } = useGetStoreMeQuery();

  const [storeLogout, { isLoading: isLogoutLoading }] =
    useStoreLogoutMutation();

  const topItems = useMemo<MenuItem[]>(() => {
    const items = Array.isArray((storeMenu as any)?.items)
      ? (((storeMenu as any).items as MenuItem[]) ?? [])
      : [];

    return [...items].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
    );
  }, [storeMenu]);
  const blocks = useMemo<Block[]>(() => {
    const menuBlocks = Array.isArray((storeMenu as any)?.blocks)
      ? (((storeMenu as any).blocks as Block[]) ?? [])
      : [];

    return [...menuBlocks].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
    );
  }, [storeMenu]);

  const isBusyMenu = topItems.length === 0 && (menuLoading || menuFetching);
  const isBusySettings = !headerLogoSrc && settingsFetching;

  const blocksByKey = useMemo(() => {
    const m = new Map<string, Block>();
    blocks
      .filter((b) => b && b.is_active !== false)
      .forEach((b) => m.set(b.block_key, b));
    return m;
  }, [blocks]);

  const getBlock = (key: string) => blocksByKey.get(key);

  const userDisplayName = useMemo(() => {
    if (!me) return "Account";
    return me.email || me.mobile_e164 || "My Account";
  }, [me]);

  const userShortName = useMemo(() => {
    if (!me) return "A";
    const raw = me.email || me.mobile_e164 || "A";
    return String(raw).trim().charAt(0).toUpperCase();
  }, [me]);

  const isLoggedIn = !!me && !meError;

  const closeMobileMenu = () => {
    const navbar = document.getElementById("navbar");
    const collapse = document.getElementById(
      "navbarSupportedContent"
    ) as HTMLElement | null;

    if (collapse?.classList.contains("show")) {
      collapse.classList.remove("show");
    }

    navbar?.classList.add("navbar-expand");
    setShowMenu("");
    setShowSubMenu("");
    setShowPageSubMenu("");
  };

  const handleLogout = async () => {
    try {
      await storeLogout().unwrap();
    } catch (error) {
      console.error("Backend logout failed:", error);
    }

    try {
      await signOut(auth);
    } catch (error) {
      console.error("Firebase logout failed:", error);
    }

    try {
      localStorage.removeItem(GUEST_KEY_STORAGE);
      localStorage.setItem(GUEST_KEY_STORAGE, createGuestKey());
    } catch (error) {
      console.error("Guest key reset failed:", error);
    }

    closeMobileMenu();
    window.location.href = "/";
  };

  const menuShow = (item: any) => {
    const windowSize = document.documentElement.clientWidth;
    if (windowSize < 992) {
      setShowMenu(showMenu !== item ? item : "");
      setShowSubMenu("");
      setShowPageSubMenu("");
    } else {
      setShowMenu("");
    }
  };

  const path = props.router.location.pathname;

  useEffect(() => {
    const initMenu = () => {
      const pathName = process.env.PUBLIC_URL + path;
      const ul = document.getElementById("navigation-menu") as HTMLElement;
      if (!ul) return;

      const items: any = ul.getElementsByTagName("a");
      const itemsArray: any = Array.from(items);
      removeActivation(itemsArray);
      const matchingMenuItem = itemsArray.find(
        (x: HTMLAnchorElement) => x.pathname === pathName
      );
      if (matchingMenuItem) activateParentDropdown(matchingMenuItem);
    };

    const removeActivation = (items: HTMLAnchorElement[]) => {
      items.forEach((item: HTMLAnchorElement) =>
        item.classList.remove("active")
      );
    };

    const activateParentDropdown = (item: HTMLAnchorElement) => {
      item.classList.add("active");
      const parentDrop: any = item.closest(".dropdown");
      if (parentDrop) {
        parentDrop.classList.add("active");
        parentDrop.querySelector(".dropdown-toggle")?.classList.add("active");
        const parentDropdown = parentDrop.parentElement.closest(".dropdown");

        if (parentDropdown) {
          parentDropdown
            .querySelector(".dropdown-toggle")
            ?.classList.add("active");
          const parentEleDropdown =
            parentDropdown.parentElement.closest(".dropdown");
          if (parentEleDropdown) {
            parentEleDropdown
              .querySelector(".dropdown-toggle")
              ?.classList.add("active");
          }
        }
      }
    };

    initMenu();
    closeMobileMenu();
  }, [path]);

  const windowScroll = () => {
    const navbar = document.getElementById("navbar");
    if (!navbar) return;
    if (
      document.body.scrollTop >= 50 ||
      document.documentElement.scrollTop >= 50
    ) {
      navbar.classList.add("is-sticky");
    } else {
      navbar.classList.remove("is-sticky");
    }
  };

  useEffect(() => {
    const onScroll = () => windowScroll();
    windowScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleShowColl = () => {
    const navbar = document.getElementById("navbar");
    const collapse = document.getElementById(
      "navbarSupportedContent"
    ) as HTMLElement | null;

    navbar && navbar.classList.remove("navbar-expand");
    if (!collapse) return;

    if (collapse.classList.contains("show")) {
      closeMobileMenu();
    } else {
      collapse.classList.add("show");
      setShowMenu("");
      setShowSubMenu("");
      setShowPageSubMenu("");
    }
  };

  const windowResizeHover = () => {
    const windowSize = document.documentElement.clientWidth;
    if (windowSize > 992) {
      const collapse = document.getElementById(
        "navbarSupportedContent"
      ) as HTMLElement | null;
      if (collapse && collapse.classList.contains("show")) {
        collapse.classList.remove("show");
      }
      setShowMenu("");
      setShowSubMenu("");
      setShowPageSubMenu("");
    }
  };

  useEffect(() => {
    window.addEventListener("resize", windowResizeHover);
    return () => window.removeEventListener("resize", windowResizeHover);
  }, []);

  const toLink = (it: MenuItem) => it.to_path || it.url || "/#";

  const sortMenuNodes = <T extends { id: number; sort_order?: number | null }>(
    items: T[]
  ) =>
    [...items].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
    );

  const sortMegaGroups = (items: MenuItem[]) =>
    [...items].sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        (a.column_no ?? 999) - (b.column_no ?? 999) ||
        a.id - b.id
    );

  const renderCatalogGroupColumn = (groups: MenuItem[]) => {
    return groups.map((group) => (
      <div key={group.id} className="mb-3">
        <ul className="dropdown-menu-list list-unstyled mb-0">
          <li>
            <p className="mb-2 text-uppercase fs-11 fw-medium text-muted menu-title">
              {group.group_title || group.label}
            </p>
          </li>
          {sortMenuNodes(
            (group.children ?? []).filter((lnk) => lnk.is_active !== false)
          )
            .map((lnk) => (
              <li className="nav-item" key={lnk.id}>
                <Link
                  to={toLink(lnk)}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  {lnk.label}
                </Link>
              </li>
            ))}
        </ul>
      </div>
    ));
  };

  const renderCatalogMega = (top: MenuItem, key: string) => {
    const left = getBlock("catalog_left_promo");
    const right = getBlock("catalog_right_brands");

    const categoryGroups = sortMegaGroups(
      (top.children ?? []).filter((g) => g.is_active !== false)
    );

    const splitIndex = Math.ceil(categoryGroups.length / 2);
    const sectionOneGroups = categoryGroups.slice(0, splitIndex);
    const sectionTwoGroups = categoryGroups.slice(splitIndex);

    return (
      <li
        className="dropdown nav-item dropdown-hover dropdown-mega-full"
        key={top.id}
      >
        <Link
          to="/#"
          className="nav-link dropdown-toggle"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            menuShow(key);
          }}
        >
          {top.label}
        </Link>

        <div
          className={
            showMenu === key ? "dropdown-menu p-0 show" : "dropdown-menu p-0"
          }
        >
          <Row className="g-0 g-lg-4">
            <Col lg={2} className="d-none d-lg-block">
              {left ? (
                <Card
                  className="rounded-start rounded-0 border-0 h-100 mb-0 overflow-hidden"
                  style={{
                    backgroundImage: `url(${left.image_url})`,
                    backgroundSize: "cover",
                  }}
                >
                  <div
                    className={`bg-overlay ${
                      left?.overlay_json?.class || "bg-light"
                    } bg-opacity-25`}
                    style={{ opacity: left?.overlay_json?.opacity ?? 0.25 }}
                  />
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <div className="text-center">
                      {left.cta_to_path ? (
                        <Link
                          to={left.cta_to_path}
                          className={`btn btn-${
                            left.cta_style || "secondary"
                          } btn-hover`}
                          onClick={closeMobileMenu}
                        >
                          {left.cta_icon ? (
                            <i
                              className={`${left.cta_icon} align-middle me-1`}
                            />
                          ) : null}
                          <span>{left.cta_label || props.t("shop-now")}</span>
                        </Link>
                      ) : null}
                    </div>
                  </Card.Body>
                </Card>
              ) : null}
            </Col>

            <Col lg={4}>
              <div className="py-3">
                {sectionOneGroups.length > 0 ? (
                  <>
                    <p className="mb-3 text-uppercase fs-11 fw-semibold text-muted">
                      Categories
                    </p>
                    {renderCatalogGroupColumn(sectionOneGroups)}
                  </>
                ) : null}
              </div>
            </Col>

            <Col lg={4}>
              <div className="py-3">
                {sectionTwoGroups.length > 0 ? (
                  <>
                    <p className="mb-3 text-uppercase fs-11 fw-semibold text-muted">
                      More Categories
                    </p>
                    {renderCatalogGroupColumn(sectionTwoGroups)}
                  </>
                ) : null}
              </div>
            </Col>

            <Col lg={2} className="d-none d-lg-block">
              {right ? (
                <div className="p-3">
                  <p className="mb-3 text-uppercase fs-11 fw-medium text-muted">
                    {right.title || props.t("top-brands")}
                  </p>
                  <Row className="g-2">
                    {sortMenuNodes(
                      (right.items ?? []).filter((bi) => bi.is_active !== false)
                    )
                      .map((bi) => (
                        <Col lg={4} key={bi.id}>
                          <Link
                            to={bi.to_path || bi.url || "/#"}
                            className="d-block p-2 border border-dashed text-center rounded-3"
                            onClick={closeMobileMenu}
                          >
                            <Image
                              src={bi.image_url || ""}
                              alt=""
                              className={
                                (right.settings_json as any)?.item_class ||
                                "avatar-sm"
                              }
                            />
                          </Link>
                        </Col>
                      ))}
                  </Row>
                </div>
              ) : null}
            </Col>
          </Row>
        </div>
      </li>
    );
  };

  const renderShopPromoMega = (top: MenuItem, key: string) => {
    const left = getBlock("shop_left_promo");

    return (
      <li className="nav-item dropdown dropdown-hover" key={top.id}>
        <Link
          className="nav-link dropdown-toggle"
          to="/#"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            menuShow(key);
          }}
        >
          {top.label}
        </Link>

        <div
          className={
            showMenu === key
              ? "dropdown-menu dropdown-mega-menu-xl dropdown-menu-center p-0 show"
              : "dropdown-menu dropdown-mega-menu-xl dropdown-menu-center p-0"
          }
        >
          <Row className="g-0 g-lg-4">
            <Col lg={5} className="d-none d-lg-block">
              {left ? (
                <Card
                  className="rounded-start rounded-0 border-0 h-100 mb-0 overflow-hidden"
                  style={{
                    backgroundImage: `url(${left.image_url})`,
                    backgroundSize: "cover",
                  }}
                >
                  <div
                    className={`bg-overlay ${
                      left?.overlay_json?.class || "bg-primary"
                    }`}
                    style={{ opacity: left?.overlay_json?.opacity ?? 0.9 }}
                  />
                  <Card.Body className="d-flex align-items-center justify-content-center position-relative">
                    <div className="text-center">
                      {left.title ? (
                        <h6 className="card-title text-white">{left.title}</h6>
                      ) : null}
                      {left.subtitle ? (
                        <p className="text-white-75">{left.subtitle}</p>
                      ) : null}
                      {left.cta_to_path ? (
                        <Link
                          to={left.cta_to_path}
                          className={`btn btn-${
                            left.cta_style || "light"
                          } btn-sm btn-hover`}
                          onClick={closeMobileMenu}
                        >
                          {left.cta_label || "Shop Now"}{" "}
                          {left.cta_icon ? (
                            <i className={`${left.cta_icon} align-middle`} />
                          ) : null}
                        </Link>
                      ) : null}
                    </div>
                  </Card.Body>
                </Card>
              ) : null}
            </Col>

            <Col lg={7}>
              <Row className="g-0 g-lg-4">
                {sortMegaGroups(
                  (top.children ?? []).filter((g) => g.is_active !== false)
                )
                  .map((group) => (
                    <Col lg={6} key={group.id}>
                      <div className="py-3">
                        <ul className="dropdown-menu-list list-unstyled mb-0">
                          <li>
                            <p className="mb-2 text-uppercase fs-11 fw-medium text-muted menu-title">
                              {group.group_title || group.label}
                            </p>
                          </li>
                          {sortMenuNodes(
                            (group.children ?? []).filter(
                              (lnk) => lnk.is_active !== false
                            )
                          )
                            .map((lnk) => (
                              <li className="nav-item" key={lnk.id}>
                                <Link
                                  to={toLink(lnk)}
                                  className="nav-link"
                                  onClick={closeMobileMenu}
                                >
                                  {lnk.label}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </Col>
                  ))}
              </Row>
            </Col>
          </Row>
        </div>
      </li>
    );
  };

  const renderDropdownList = (top: MenuItem, key: string) => {
    return (
      <li className="nav-item dropdown dropdown-hover" key={top.id}>
        <Link
          className="nav-link dropdown-toggle"
          to="/#"
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            menuShow(key);
          }}
        >
          {top.label}
        </Link>

        <ul
          className={
            showMenu === key
              ? "dropdown-menu dropdown-menu-md dropdown-menu-center dropdown-menu-list submenu show"
              : "dropdown-menu dropdown-menu-md dropdown-menu-center dropdown-menu-list submenu"
          }
        >
          {sortMenuNodes(
            (top.children ?? []).filter((c) => c.is_active !== false)
          )
            .map((c) => (
              <li key={c.id} className="nav-item">
                <Link
                  to={toLink(c)}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  {c.label}
                </Link>
              </li>
            ))}
        </ul>
      </li>
    );
  };

  const renderTopItem = (top: MenuItem) => {
    const ui = top.meta_json?.ui;
    const key = top.meta_json?.key || String(top.id);

    if (!top.children || top.children.length === 0) {
      return (
        <li key={top.id} className="nav-item">
          <Link
            className="nav-link"
            to={toLink(top)}
            style={linkStyle}
            onClick={closeMobileMenu}
          >
            {top.label}
          </Link>
        </li>
        );
      }

    if (ui === "mega_full") return renderCatalogMega(top, key);
    if (ui === "promo_mega") return renderShopPromoMega(top, key);
    return renderDropdownList(top, key);
  };

  return (
    <React.Fragment>
      <style>
        {`
          @keyframes headerSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: 0 0; }
          }
        `}
      </style>

      <Navbar
        className="navbar-expand-lg ecommerce-navbar"
        id="navbar"
        expanded={false}
        style={navbarStyle}
      >
        <Container>
          <Navbar.Brand href="/#" className="d-none d-lg-block">
            {headerLogoSrc ? (
              <>
                <div className="logo-dark">
                  <Image src={headerLogoSrc} alt="Logo" height="50" />
                </div>
                <div className="logo-light">
                  <Image src={headerLogoLightSrc} alt="Logo" height="50" />
                  <span className="logo-txt"></span>
                </div>
              </>
            ) : isBusySettings ? (
              <HeaderLogoSkeleton />
            ) : null}
          </Navbar.Brand>

          <Button
            className="btn btn-soft-primary btn-icon d-lg-none collapsed"
            aria-controls="navbarSupportedContent"
            onClick={handleShowColl}
          >
            <i className="bi bi-list fs-20"></i>
          </Button>

          <div className="d-flex d-lg-none align-items-center justify-content-center flex-grow-1 px-2">
            <Link
              to="/"
              className="navbar-brand p-0 m-0 d-flex align-items-center justify-content-center"
              onClick={closeMobileMenu}
            >
              {headerLogoSrc ? (
                <Image
                  src={headerLogoSrc}
                  alt="Logo"
                  height="40"
                  className="d-block"
                />
              ) : isBusySettings ? (
                <HeaderLogoSkeleton height={40} />
              ) : null}
            </Link>
          </div>

          <Navbar.Collapse id="navbarSupportedContent">
            <div className="d-block d-lg-none px-3 pt-3 pb-2">
              <div style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }} />
            </div>

            <Nav
              as="ul"
              className="mx-lg-auto mb-2 mb-lg-0"
              id="navigation-menu"
            >
              {isBusyMenu ? (
                <HeaderMenuSkeleton />
              ) : (
                topItems.map((top) => renderTopItem(top))
              )}
            </Nav>
          </Navbar.Collapse>

          <div
            className="bg-overlay navbar-overlay"
            onClick={closeMobileMenu}
            aria-hidden="true"
          ></div>

          <div className="d-flex align-items-center ms-auto">
            <Button
              type="button"
              className="btn btn-icon btn-topbar btn-ghost-dark rounded-circle text-muted"
              data-bs-toggle="modal"
              data-bs-target="#searchModal"
              onClick={handleShow}
              style={secondaryColor ? { color: secondaryColor } : undefined}
            >
              <i className="bx bx-search fs-22"></i>
            </Button>
            <SearchModal show={show} handleClose={handleClose} />

            <div className="topbar-head-dropdown ms-1 header-item">
              <Button
                type="button"
                className="btn btn-icon btn-topbar btn-ghost-dark rounded-circle text-muted"
                data-bs-toggle="offcanvas"
                data-bs-target="#ecommerceCart"
                aria-controls="ecommerceCart"
                onClick={handlecardShow}
                style={secondaryColor ? { color: secondaryColor } : undefined}
              >
                <i className="ph-shopping-cart fs-18"></i>
                <span className="position-absolute topbar-badge cartitem-badge fs-10 translate-middle badge rounded-pill bg-danger">
                  {cartItemsCount}
                </span>
              </Button>
            </div>

            <Button
              type="button"
              className="btn btn-icon btn-topbar btn-ghost-dark rounded-circle text-muted ms-2 header-item"
              onClick={handleThemeToggle}
              style={secondaryColor ? { color: secondaryColor } : undefined}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              <i
                className={`${isDarkMode ? "bi bi-moon-stars" : "bi bi-sun"} align-middle fs-20`}
              ></i>
            </Button>

            <div className="dropdown header-item dropdown-hover-end">
              <Dropdown>
                <Dropdown.Toggle
                  id="page-header-user-dropdown"
                  bsPrefix="btn"
                  className="btn btn-icon btn-topbar btn-link rounded-circle d-flex align-items-center justify-content-center"
                  as="a"
                >
                  <div
                    className="rounded-circle header-profile-user d-flex align-items-center justify-content-center fw-semibold"
                    style={{
                      width: 32,
                      height: 32,
                      overflow: "hidden",
                      backgroundColor: isDarkMode ? "#ffffff" : "#212529",
                      color: isDarkMode ? "#212529" : "#f8f9fa",
                    }}
                  >
                    {isLoggedIn ? (
                      userShortName
                    ) : (
                      <Image
                        className="rounded-circle header-profile-user"
                        src={avtar1}
                        alt="Header Avatar"
                      />
                    )}
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  {meFetching ? (
                    <Dropdown.Item as="button" disabled>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </Dropdown.Item>
                  ) : isLoggedIn ? (
                    <>
                      <Dropdown.Header>
                        <div
                          className="fw-semibold"
                          style={{ color: isDarkMode ? "#f8f9fa" : undefined }}
                        >
                          {userDisplayName}
                        </div>
                        <div
                          className="small"
                          style={{ color: isDarkMode ? "rgba(248,249,250,0.75)" : undefined }}
                        >
                          {me?.role}
                        </div>
                      </Dropdown.Header>

                      <Dropdown.Item
                        as={Link}
                        to="/account"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-person text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">My Account</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/shop/orderhistory"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-cart4 text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">Order History</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/shop/order"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-truck text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">Track Orders</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/ecommerce-faq"
                        onClick={closeMobileMenu}
                      >
                        <i className="mdi mdi-lifebuoy text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">Help</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        as="button"
                        onClick={handleLogout}
                        disabled={isLogoutLoading}
                      >
                        <i className="bi bi-box-arrow-right text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">
                          {isLogoutLoading ? "Logging out..." : "Logout"}
                        </span>
                      </Dropdown.Item>
                    </>
                  ) : (
                    <>
                      <Dropdown.Item
                        as={Link}
                        to="/auth-signin-basic"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-box-arrow-in-right text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">Login</span>
                      </Dropdown.Item>

                      <Dropdown.Item
                        as={Link}
                        to="/auth-signin-basic"
                        onClick={closeMobileMenu}
                      >
                        <i className="bi bi-person text-muted fs-16 align-middle me-1"></i>{" "}
                        <span className="align-middle">My Account</span>
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Container>
      </Navbar>

      <CardModal show={card} handleClose={handlecardClose} />
    </React.Fragment>
  );
};

export default withRouter(withTranslation()(Header));
